import { useMutation } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { useNavigate } from "react-router-dom";

import type {
  LoginCredentials,
  RegisterCredentials,
  SessionResponse,
} from "./api.dto";
import { useSessionStore } from "@/entities/session";
import { api } from "@/shared/api";
import { useToast } from "@/shared/hooks/useToast";

export const useRegister = () => {
  const { setUser, setAccessToken, setRefreshToken } = useSessionStore();
  const { toast } = useToast();
  const navigate = useNavigate();

  return useMutation<
    AxiosResponse,
    AxiosError,
    RegisterCredentials,
    SessionResponse
  >({
    mutationKey: ["register"],
    mutationFn: (credentials) => {
      return api.post("/authorization/register", credentials);
    },
    onSuccess: (response) => {
      const accessToken = response.data.accessToken;
      const refreshToken = response.data.refreshToken;

      setAccessToken(accessToken);
      setRefreshToken(refreshToken);

      setUser(response.data.user);

      toast({
        title: "Удачно!",
        description: "Вы зарегестрировались",
      });

      navigate("/");
    },
    onError: (error) => {
      toast({
        title: "Ошибка!",
        variant: "destructive",
        description: error.response?.data as string,
      });
    },
  });
};

export const useLogin = () => {
  const { setUser, setAccessToken, setRefreshToken } = useSessionStore();
  const { toast } = useToast();
  const navigate = useNavigate();

  return useMutation<AxiosResponse, AxiosError, LoginCredentials, Response>({
    mutationKey: ["login"],
    mutationFn: async (credentials) => {
      const response = await api.post("/authorization/login", credentials);

      return response;
    },
    onSuccess: (response) => {
      if (response.status === 200) return;

      const accessToken = response.data.accessToken;
      const refreshToken = response.data.refreshToken;

      setAccessToken(accessToken);
      setRefreshToken(refreshToken);

      setUser(response.data.user);

      toast({
        title: "Удачно!",
        description: "Вы вошли в систему",
      });

      navigate("/");
    },
    onError: (error) => {
      toast({
        title: "Ошибка!",
        variant: "destructive",
        description: (error.response?.data as AxiosError)?.message || "",
      });
    },
  });
};
